<template>
  <div class="sorted-item" 
      :class="itemClasses"
      @click="handleItemClick">
    <div class="grid rounded-[6px]" :class="gridLayoutClass">

      <!-- 選取模式的 checkbox -->
      <div v-if="selectable" class="checkbox-item w-full h-full flex justify-center items-center " :class="{ 'selected': isSelected }">
        <input
          type="checkbox"
          :id="`checkbox-${item.id}`"
          :checked="isSelected"
          @change="handleToggleSelect"
          @click.stop
        />
        <label :for="`checkbox-${item.id}`"></label>
        <div class="check"></div>
      </div>

      <!-- 拖拽圖標和縮圖區域 -->
      <div class='p-2 flex justify-between items-center'>
        <img v-if="showSortableIcon" 
             src="../../images/icons/sortable_icon.svg" 
             class="w-8 h-8 object-contain sortable-handle cursor-move"/>
        <div v-if="orderType === 'custom_order' && !selectable">{{ index }}</div>

        <!-- 縮圖 -->
        <div class="w-[72px] h-[72px] rounded-8px overflow-hidden relative">
          <!-- PV 標籤 -->
          <div v-if="item.phrozen_verified" 
               class="w-10 h-4 flex items-center justify-center absolute left-1/2 bottom-[15%] translate-x-[-50%] bg-red rounded text-14px bg-[#E60044]">
            <img src="../../images/home/phrozen_verified_white.svg" class="w-3 h-3 object-contain"/>
            PV
          </div>
          
          <!-- 免費標籤 -->
          <div v-if="item.price == 0" 
               class="w-10 h-4 flex items-center justify-center absolute left-1/2 bottom-[15%] translate-x-[-50%] rounded text-14px text-[#19791D] bg-[#2CFF67]">
            {{ $t('post_item.free') }}
          </div>
          
          <img :src="item.cover_image_src || item.preview_posts?.[0]?.cover_image_src" 
               :alt="item.title" />
        </div>
      </div>

      <!-- 信息區域 -->
      <div class='py-2'>
        <div class="flex flex-col h-full pr-3 break-word">
          <!-- 標題 -->
          <div class="post_title text-14px font-medium hover:underline">
            <a :href="postUrl">{{ item.title }}</a>
          </div>

          <!-- 時間和價格信息 -->
          <div class="timestamp text-12px text-grey-5">
            <div class="created">
              Create: {{ formatDate(item.created_at) }}
            </div>
            <div class="price">
              Price: {{ formatCurrency(item.price) }}
            </div>
          </div>
        </div>
      </div>

      <!-- 操作按鈕 -->
      <sorted-action-buttons
        v-if="!selectable"
        :order-type="orderType"
        :show-pin-icon="showPinIcon"
        :is-pinned="isPinned"
        @move-to-top="handleMoveToTop"
        @move-to-bottom="handleMoveToBottom"
        @toggle-pin="handleTogglePin"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue';
import { useSortableItem } from '../utils/useSortableItem.js';

export default defineComponent({
  name: 'PostSortItem',
  props: {
    item: {
      type: Object,
      required: true,
    },
    isPinned: {
      type: Boolean,
      default: false
    },
    isSortable: {
      type: Boolean,
      default: false
    },
    showPinIcon: {
      type: Boolean,
      default: true
    },
    index: {
      type: Number,
      default: 0
    },
    orderType: {
      type: String,
      required: false,
      default: 'published_date'
    },
    selectable: {
      type: Boolean,
      default: false
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    showMetadata: {
      type: Boolean,
      default: true
    }
  },
  emits: ['moveToTop', 'moveToBottom', 'moveToPin'],
  
  setup(props, { emit }) {
    const {
      formatDate,
      formatCurrency,
      handleMoveToTop,
      handleMoveToBottom,
      handleTogglePin,
      showSortableIcon
    } = useSortableItem(props, emit);

    // 選取功能處理
    const handleToggleSelect = () => {
      emit('toggleSelect', props.item.id);
    };

    const handleItemClick = () => {
      if (props.selectable) {
        emit('toggleSelect', props.item.id);
      }
    };

    // Post 特有的樣式
    const itemClasses = computed(() => {    
      return {
        'bg-gray-400': props.isPinned,
        'bg-gray-600': !props.isPinned
      };
    });

    // Post 特有的網格布局
    const gridLayoutClass = computed(() => {
      if (props.selectable) {
        // 選取模式：checkbox + 圖片 + 信息
        return 'grid-cols-[36px_72px_1fr]';
      }
      
      // 排序模式：原來的邏輯
      return props.orderType === 'custom_order'
        ? (props.isSortable 
            ? 'grid-cols-[152px_1fr_128px]' 
            : 'grid-cols-[100px_1fr_128px]')
        : 'grid-cols-[80px_1fr_128px]';
    });

    // Post URL
    const postUrl = computed(() => 
      props.item.visible 
        ? `/posts/${props.item.id}` 
        : `/posts/${props.item.id}/preview`
    );

    return {
      itemClasses,
      gridLayoutClass,
      postUrl,
      formatDate,
      formatCurrency,
      handleMoveToTop,
      handleMoveToBottom,
      handleTogglePin,
      handleToggleSelect,
      handleItemClick,
      showSortableIcon
    };
  }
});
</script>